import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useStore } from "../lib/storeContext";
import { AssetImage } from "../components/AssetImage";
import { Breadcrumbs, EmptyState, SectionTitle, Spinner } from "./ui";
import { pluralize } from "../lib/util";
import { RenameButton } from "./RenameButton";
import { DeleteButton } from "./DeleteButton";
import { SharePanel } from "./SharePanel";
import { WholeHomeSection } from "./WholeHomeSection";

export function ProjectDetailView() {
  const { projectId = "" } = useParams();
  const navigate = useNavigate();
  const { store, rev, loading } = useStore();
  const [shareOpen, setShareOpen] = useState(false);
  void rev;

  if (loading) return <Spinner />;
  const project = store.get<import("../lib/types").ProjectPayload>("PROJECT", projectId);
  if (!project) return <EmptyState title="Project not found" icon="🔍" />;

  const rooms = store.rooms(projectId);
  const membership = store.membership(projectId); // set ⇒ shared WITH me (editor/viewer)
  const { latitude, longitude, address } = project.payload;
  const located = latitude != null && longitude != null;

  return (
    <div className="space-y-7">
      <Breadcrumbs items={[{ label: "Projects", to: "/" }, { label: project.payload.name || "Project" }]} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold text-ink">{project.payload.name || "Untitled"}</h1>
            <RenameButton type="PROJECT" id={projectId} current={project.payload.name} />
            <DeleteButton type="PROJECT" id={projectId} what="project" onDeleted={() => navigate("/")} />
            {membership && <span className="chip capitalize">{membership.role}</span>}
            <button
              className="btn-ghost text-sm"
              title={membership ? "Sharing details" : "Share this project"}
              onClick={() => setShareOpen(true)}
            >
              {membership ? "Shared" : "⤴ Share"}
            </button>
          </div>
          {membership && (
            <p className="text-sm text-muted">Shared by {membership.ownerName || "the owner"}</p>
          )}
          {address && <p className="text-sm text-muted">{address}</p>}
          {project.payload.details && <p className="mt-1 max-w-2xl text-sm text-ink/70">{project.payload.details}</p>}
        </div>
      </div>

      {shareOpen && <SharePanel projectId={projectId} onClose={() => setShareOpen(false)} />}

      {located && (
        <div className="overflow-hidden rounded-card shadow-card">
          {/* Real, interactive map via Google's keyless embed (no API key / dependency). */}
          <iframe
            title="Property location"
            className="block h-56 w-full border-0"
            loading="lazy"
            src={`https://maps.google.com/maps?q=${latitude},${longitude}&z=15&output=embed`}
          />
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center justify-between bg-white px-4 py-2 transition hover:bg-cream"
            title="Open in Google Maps"
          >
            <span className="text-sm font-medium text-ink">{address || `${latitude}, ${longitude}`}</span>
            <span className="text-sm font-semibold text-accent group-hover:underline">Open in Maps ↗</span>
          </a>
        </div>
      )}

      <WholeHomeSection projectId={projectId} />

      <section>
        <SectionTitle>{pluralize(rooms.length, "Room")}</SectionTitle>
        {rooms.length === 0 ? (
          <EmptyState title="No rooms yet" subtitle="Add rooms in the app to see them here." icon="🚪" />
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {rooms.map((room) => {
              const photos = store.photos(room.id);
              const cover = photos[0]?.assets[0]?.s3Key;
              const scanCount = store.scans(room.id).length;
              return (
                <div key={room.id} className="relative">
                  <Link to={`/rooms/${room.id}`} className="card group block overflow-hidden transition hover:shadow-lift">
                    <div className="aspect-[4/3] bg-cream">
                      {cover ? (
                        <AssetImage s3Key={cover} alt={room.payload.name} className="h-full w-full" />
                      ) : (
                        <div className="flex h-full items-center justify-center text-3xl opacity-40">🛋️</div>
                      )}
                    </div>
                    <div className="p-3">
                      <div className="truncate font-semibold text-ink group-hover:text-accent">{room.payload.name}</div>
                      <div className="text-xs text-muted">
                        {pluralize(photos.length, "photo")}
                        {scanCount > 0 && ` · ${pluralize(scanCount, "scan")}`}
                      </div>
                    </div>
                  </Link>
                  <div className="absolute right-1 top-1 rounded-full bg-white/85 backdrop-blur">
                    <DeleteButton type="ROOM" id={room.id} what="room" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
